/**
 * 全屏 QR 扫码器 — getUserMedia 后置摄像头 + jsQR 逐帧解码。
 *
 * 桌面 QR 内容是 `https://dispatch.hiq.earth/pair?code=XXXXXXXXXX`，
 * 解出后抽 `code` 回调。相机不可用 / 拒绝授权时降级到手动输入配对码。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import jsQR from 'jsqr'

import { useLang } from '../contexts/LanguageContext'
import { IconClose } from './icons'

/** 从扫码结果里抽配对码：URL 形式取 ?code=，否则当裸码。*/
export function extractPairCode(raw: string): string | null {
  const s = raw.trim()
  try {
    const code = new URL(s).searchParams.get('code')
    if (code && /^[A-Z0-9]{10}$/i.test(code)) return code.toUpperCase()
  } catch {
    // 不是 URL — 往下当裸码处理
  }
  return /^[A-Z0-9]{10}$/i.test(s) ? s.toUpperCase() : null
}

type Phase = 'starting' | 'scanning' | 'denied' | 'nocamera'

interface Props {
  onResult: (code: string) => void
  onClose: () => void
}

export default function QrScanner({ onResult, onClose }: Props) {
  const { t } = useLang()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const rafRef = useRef<number | null>(null)
  const doneRef = useRef(false)

  const [phase, setPhase] = useState<Phase>('starting')
  const [manual, setManual] = useState('')
  const [manualError, setManualError] = useState(false)

  const finish = useCallback(
    (code: string) => {
      if (doneRef.current) return
      doneRef.current = true
      onResult(code)
    },
    [onResult],
  )

  useEffect(() => {
    let cancelled = false

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setPhase('nocamera')
        return
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        const video = videoRef.current
        if (!video) return
        video.srcObject = stream
        await video.play()
        setPhase('scanning')
        tick()
      } catch (err) {
        const name = (err as Error)?.name
        setPhase(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'nocamera')
      }
    }

    function tick() {
      rafRef.current = requestAnimationFrame(tick)
      const video = videoRef.current
      const canvas = canvasRef.current
      if (!video || !canvas || video.readyState < video.HAVE_CURRENT_DATA) return
      const w = video.videoWidth
      const h = video.videoHeight
      if (!w || !h) return
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx) return
      ctx.drawImage(video, 0, 0, w, h)
      const img = ctx.getImageData(0, 0, w, h)
      const found = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' })
      if (found?.data) {
        const code = extractPairCode(found.data)
        if (code) finish(code)
      }
    }

    void start()
    return () => {
      cancelled = true
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
  }, [finish])

  const submitManual = () => {
    const code = extractPairCode(manual)
    if (code) {
      finish(code)
    } else {
      setManualError(true)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black reveal-in">
      {/* 摄像头画面 */}
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        playsInline
        muted
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* 关闭 */}
      <button
        onClick={onClose}
        aria-label={t('common.close')}
        className="absolute right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        <IconClose className="h-5 w-5" />
      </button>

      {phase === 'scanning' && (
        <>
          {/* 取景框 — box-shadow 把框外压暗 */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              className="relative h-64 w-64 rounded-2xl"
              style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.62)' }}
            >
              <Corner className="left-0 top-0 border-l-2 border-t-2" />
              <Corner className="right-0 top-0 border-r-2 border-t-2" />
              <Corner className="bottom-0 left-0 border-b-2 border-l-2" />
              <Corner className="bottom-0 right-0 border-b-2 border-r-2" />
              <div className="absolute inset-x-2 top-0 h-px bg-accent-light/80 shadow-[0_0_8px_2px_rgba(251,191,36,0.5)] [animation:scanSweep_2.4s_var(--ease-out)_infinite]" />
            </div>
          </div>
          <p
            className="absolute inset-x-0 text-center text-sm text-white/85"
            style={{ top: 'calc(50% + 9.5rem)' }}
          >
            {t('scan.hint')}
          </p>
        </>
      )}

      {phase === 'starting' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-sm text-white/70">{t('scan.starting')}</p>
        </div>
      )}

      {(phase === 'denied' || phase === 'nocamera') && (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
          <p className="mb-1 font-heading text-lg text-white">
            {phase === 'denied' ? t('scan.denied') : t('scan.noCamera')}
          </p>
          <p className="text-sm text-white/60">{t('scan.fallbackHint')}</p>
        </div>
      )}

      {/* 手动输入兜底 */}
      <div
        className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/85 to-transparent px-5 pt-10"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
      >
        <label className="mb-2 block text-xs text-white/55">{t('scan.manualLabel')}</label>
        <div className="flex gap-2">
          <input
            value={manual}
            onChange={(e) => {
              setManual(e.target.value)
              setManualError(false)
            }}
            placeholder={t('scan.manualPlaceholder')}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={10}
            className="min-w-0 flex-1 rounded-lg border border-white/15 bg-white/10 px-3 py-2.5 font-mono text-base uppercase tracking-widest text-white placeholder:text-white/35 placeholder:normal-case placeholder:tracking-normal focus:border-accent focus:outline-none"
          />
          <button
            onClick={submitManual}
            disabled={!manual.trim()}
            className="rounded-lg bg-accent px-4 text-sm font-medium text-white transition-colors disabled:bg-white/10 disabled:text-white/35"
          >
            {t('scan.pairBtn')}
          </button>
        </div>
        {manualError && <p className="mt-1.5 text-xs text-error">{t('scan.manualError')}</p>}
      </div>
    </div>
  )
}

function Corner({ className }: { className: string }) {
  return <div className={`absolute h-6 w-6 rounded-[3px] border-accent-light ${className}`} />
}
